import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import { useToast } from '../../context/ToastContext';
import { useGoals } from '../../hooks/useGoals';
import { Rubric } from '../../hooks/useRubric';
import { useCareerCards } from '../../hooks/useCareerCards';
import { useNotes } from '../../hooks/useNotes';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../context/AuthContext';
import { IoCloseOutline, IoCheckmarkOutline, IoLinkOutline } from 'react-icons/io5';

interface EvidenceLinkingModalProps {
  cardId: string;
  sourceType?: 'career_card' | 'note';
  focusId: string;
  activeRubric?: Rubric | null;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface ExistingLink {
  id: string;
  target_id: string;
  target_type: 'goal' | 'rubric_item' | 'rubric_criterion';
  metadata?: any;
}

const EvidenceLinkingModal: React.FC<EvidenceLinkingModalProps> = ({ 
  cardId, 
  sourceType = 'career_card',
  focusId, 
  activeRubric,
  isOpen, 
  onClose, 
  onComplete 
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { goals } = useGoals(focusId);
  const { cards } = useCareerCards();
  const { notes } = useNotes();
  
  const [selectedGoals, setSelectedGoals] = useState<Set<string>>(new Set());
  const [selectedRubricItems, setSelectedRubricItems] = useState<Set<string>>(new Set());
  const [existingLinks, setExistingLinks] = useState<ExistingLink[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';

  const currentItem = sourceType === 'career_card' 
    ? cards?.find(c => c.id === cardId)
    : notes?.find(n => n.id === cardId);
  const itemTitle = currentItem?.title || (sourceType === 'career_card' ? 'this card' : 'this note');

  // Fetch existing links
  useEffect(() => {
    if (!isOpen || !cardId) return;

    const fetchLinks = async () => {
      const { data, error } = await supabase
        .from('content_links')
        .select('*')
        .eq('source_id', cardId)
        .eq('source_type', sourceType)
        .in('target_type', ['goal', 'rubric_criterion']);

      if (!error && data) {
        setExistingLinks(data);
        
        // Set initial selections
        const goalIds = new Set(data.filter(l => l.target_type === 'goal').map(l => l.target_id));
        const rubricIds = new Set(data.filter(l => l.target_type === 'rubric_item' || l.target_type === 'rubric_criterion').map(l => l.target_id));
        
        setSelectedGoals(goalIds);
        setSelectedRubricItems(rubricIds);
      }
    };

    fetchLinks();
  }, [isOpen, cardId]);

  const toggleGoal = (goalId: string) => {
    const newSelection = new Set(selectedGoals);
    if (newSelection.has(goalId)) {
      newSelection.delete(goalId);
    } else {
      newSelection.add(goalId);
    }
    setSelectedGoals(newSelection);
  };

  const toggleRubricItem = (criterionId: string) => {
    const newSelection = new Set(selectedRubricItems);
    if (newSelection.has(criterionId)) {
      newSelection.delete(criterionId);
    } else {
      newSelection.add(criterionId);
    }
    setSelectedRubricItems(newSelection);
  };

  const handleSave = async () => {
    if (!user) {
      showToast('You must be logged in to save links', 'error');
      return;
    }
    
    setIsSaving(true);
    try {
      // Delete all existing links for this card
      const { error: deleteError } = await supabase
        .from('content_links')
        .delete()
        .eq('source_id', cardId)
        .eq('source_type', sourceType)
        .in('target_type', ['goal', 'rubric_criterion']);

      if (deleteError) throw deleteError;

      // Create new links
      const newLinks = [];

      // Goal links
      for (const goalId of selectedGoals) {
        const goal = goals?.find(g => g.id === goalId);
        if (goal) {
          newLinks.push({
            source_id: cardId,
            source_type: sourceType,
            target_id: goalId,
            target_type: 'goal',
            user_id: user.id,
            link_type: 'supports',
            metadata: { goal_text: goal.goal_text }
          });
        }
      }

      // Rubric criterion links
      for (const criterionId of selectedRubricItems) {
        // Find the criterion details
        let criterionText = '';
        let categoryName = '';
        
        if (activeRubric) {
          for (const category of activeRubric.categories) {
            if (category.criteria_items) {
              const criterion = category.criteria_items.find(c => c.id === criterionId);
              if (criterion) {
                criterionText = criterion.text;
                categoryName = category.name;
                break;
              }
            }
          }
        }
        
        if (criterionText) {
          newLinks.push({
            source_id: cardId,
            source_type: sourceType,
            target_id: criterionId,
            target_type: 'rubric_criterion',
            user_id: user.id,
            link_type: 'demonstrates',
            metadata: { 
              category_name: categoryName,
              rubric_text: criterionText,
              focus_id: focusId
            }
          });
        }
      }

      if (newLinks.length > 0) {
        const { error: insertError } = await supabase
          .from('content_links')
          .insert(newLinks);

        if (insertError) throw insertError;
      }

      showToast('Links updated successfully', 'success');
      onComplete();
    } catch (error) {
      console.error('Error saving links:', error);
      showToast('Failed to save links', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !currentItem) return null;

  // Normalize rubric data
  const rubricCategories = activeRubric?.categories || [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className={`relative w-full max-w-3xl max-h-[80vh] ${bgColor} rounded-xl shadow-2xl border ${borderColor} flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
          <div>
            <h2 className={`text-xl font-semibold ${textColor}`}>
              Link Evidence
            </h2>
            <p className={`text-sm ${mutedTextColor} mt-1`}>
              Connect "{itemTitle}" to your goals and evaluation criteria
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg hover:bg-gray-700 transition-colors ${mutedTextColor}`}
          >
            <IoCloseOutline className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Goals Section */}
            <div>
              <h3 className={`text-sm font-semibold ${textColor} mb-3 flex items-center gap-2`}>
                <span className="text-lg">🎯</span> Personal Goals
              </h3>
              <div className="space-y-2">
                {goals?.map(goal => {
                  const isSelected = selectedGoals.has(goal.id);
                  return (
                    <label
                      key={goal.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-300'
                          : cardBg
                      } border ${borderColor}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleGoal(goal.id)}
                        className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${textColor}`}>{goal.goal_text}</span>
                    </label>
                  );
                })}
                {(!goals || goals.length === 0) && (
                  <p className={`text-sm ${mutedTextColor} italic`}>No goals set up yet</p>
                )}
              </div>
            </div>

            {/* Rubric Section */}
            <div>
              <h3 className={`text-sm font-semibold ${textColor} mb-3 flex items-center gap-2`}>
                <span className="text-lg">📋</span> Evaluation Criteria
              </h3>
              <div className="space-y-4">
                {rubricCategories.map(category => (
                  <div key={category.id}>
                    <h4 className={`text-xs font-medium ${mutedTextColor} mb-2 uppercase`}>
                      {category.name}
                    </h4>
                    <div className="space-y-1">
                      {(category.criteria_items || category.criteria).map((criterion: any, index: number) => {
                        // Use actual criterion ID if available, otherwise fall back to old format
                        const criterionId = criterion.id || `${focusId}_${category.id}_${index}`;
                        const isSelected = selectedRubricItems.has(criterionId);
                        const criterionText = typeof criterion === 'string' ? criterion : criterion.text || '';
                        
                        return (
                          <label
                            key={criterionId}
                            className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                              isSelected 
                                ? darkMode ? 'bg-green-900/20' : 'bg-green-50'
                                : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRubricItem(criterionId)}
                              className="w-4 h-4 text-green-500 rounded focus:ring-2 focus:ring-green-500"
                            />
                            <span className={`text-sm ${textColor}`}>{criterionText}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {rubricCategories.length === 0 && (
                  <p className={`text-sm ${mutedTextColor} italic`}>No evaluation rubric set up yet</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-6 border-t ${borderColor}`}>
          <p className={`text-sm ${mutedTextColor}`}>
            {selectedGoals.size + selectedRubricItems.size} items selected
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                darkMode 
                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                !isSaving
                  ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Saving...
                </>
              ) : (
                <>
                  <IoCheckmarkOutline className="w-5 h-5" />
                  Save Links
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EvidenceLinkingModal;